import React, { useEffect, useState } from "react";
import { safeUrl } from "../../utils/safeUrl";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import {
  FiArrowLeft, FiUser, FiZap, FiLayers, FiGitBranch, FiAlertTriangle,
  FiFlag, FiShield, FiHelpCircle, FiSmartphone, FiActivity, FiBell, FiAward,
  FiCheckCircle, FiXCircle, FiCopy
} from "react-icons/fi";
import PlatformVerificationCards, { PLATFORMS } from "./PlatformVerificationCards";
import UserApplicationsTable from "./UserApplicationsTable";
import SendNotificationModal from "./SendNotificationModal";
import NotificationHistoryTab from "./NotificationHistoryTab";
import PhoneUserPanel from "../sms-verification/PhoneUserPanel";
import { usePermission } from "../../context/PermissionContext";
import toast from "react-hot-toast";
import { REVERT_NOTE_MAX, REVERT_NOTE_MIN, fetchUserKyc, revertUserKycStep } from "../../utils/kycSettingsApi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-sm font-medium text-gray-800 break-all ${mono ? "font-mono text-xs" : ""}`}>
      {value === null || value === undefined || value === "" ? "—" : String(value)}
    </p>
  </div>
);

const Section = ({ title, icon: Icon, iconColor = "text-blue-500", iconBg = "bg-blue-50", children, empty = false, emptyLabel = "No data available" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
        <Icon size={14} className={iconColor} />
      </div>
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="px-6 py-5">
      {empty ? <p className="text-sm text-gray-400">{emptyLabel}</p> : children}
    </div>
  </div>
);

const Badge = ({ value, trueLabel = "Yes", falseLabel = "No", trueColor = "green" }) => {
  const on = Boolean(value);
  const colorMap = {
    green: "bg-green-100 text-green-700",
    gray: "bg-gray-100 text-gray-500",
    orange: "bg-orange-100 text-orange-600",
    blue: "bg-blue-100 text-blue-600",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[on ? trueColor : "gray"]}`}>
      {on ? trueLabel : falseLabel}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const cls = s === "approved"
    ? "bg-green-100 text-green-700"
    : (s === "rejected" || s === "reject")
      ? "bg-red-100 text-red-600"
      : "bg-amber-100 text-amber-600";
  return <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full capitalize ${cls}`}>{status || "pending"}</span>;
};

/**
 * user_kyc has no single `status` column — each of the 6 steps carries its
 * own (open / pending / locked / completed / rejected), so a KYC row is a
 * funnel, not a single verdict.
 */
const KYC_STEPS = [
  { n: 1, label: "Mining Streak", desc: "5-day mining streak" },
  { n: 2, label: "Referral Requirement", desc: "3 successful referrals" },
  { n: 3, label: "Profile Verification", desc: "Username, country & email" },
  { n: 4, label: "Phone Verification", desc: "" },
  { n: 5, label: "Social Media Verification", desc: "" },
  { n: 6, label: "Selfie & AI Face Match", desc: "" },
];

const KycStepBadge = ({ status }) => {
  const s = String(status || "locked").toLowerCase();
  const map = {
    completed: "bg-green-100 text-green-700",
    open: "bg-blue-100 text-blue-600",
    pending: "bg-amber-100 text-amber-600",
    rejected: "bg-red-100 text-red-600",
    locked: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full capitalize ${map[s] || map.locked}`}>
      {s}
    </span>
  );
};

const StatCard = ({ label, value, sub, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    green: "bg-green-50 border-green-100 text-green-700",
    orange: "bg-orange-50 border-orange-100 text-orange-600",
    purple: "bg-purple-50 border-purple-100 text-purple-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value ?? "—"}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
};

const SimpleTable = ({ columns, rows, rowKey, scrollable = false }) => {
  if (!rows || rows.length === 0) return <p className="text-sm text-gray-400">No records found.</p>;
  return (
    <div className={`overflow-x-auto -mx-6 ${scrollable ? "max-h-[420px] overflow-y-auto" : ""}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-6 py-2.5 ${c.align === "right" ? "text-right" : ""} ${scrollable ? "sticky top-0 bg-gray-50 z-10" : ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row, idx) => (
            <tr key={rowKey ? row[rowKey] ?? idx : idx} className="hover:bg-gray-50 transition">
              {columns.map((c) => (
                <td key={c.key} className={`px-6 py-2.5 text-gray-700 ${c.align === "right" ? "text-right" : ""}`}>
                  {c.render ? c.render(row) : (row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatDate = (dateStr, withTime = true) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date)) return String(dateStr);
  return withTime
    ? date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const TABS = [
  { key: "account",    label: "Account",      icon: FiUser },
  { key: "mining",     label: "Mining",       icon: FiZap },
  { key: "creatorHub", label: "Creator Hub",  icon: FiLayers },
  { key: "referral",   label: "Referral",     icon: FiGitBranch },
  { key: "strikes",    label: "Strikes",      icon: FiAlertTriangle },
  { key: "pioneer",    label: "Pioneer",      icon: FiFlag },
  { key: "kyc",        label: "KYC",          icon: FiShield },
  { key: "support",    label: "Support",      icon: FiHelpCircle },
  { key: "devices",    label: "Devices",      icon: FiSmartphone },
  { key: "activity",   label: "Activity",     icon: FiActivity },
  { key: "notificationHistory", label: "Notification History", icon: FiBell },
];

const UserFullDetail = () => {
  const { userId } = useParams();
  // No provider in some tests: treat that as "cannot see" rather than crash.
  const permission = usePermission();
  const canSeePhone = !!permission && permission.hasPermission("sms-verification", "view");
  const canRevertKyc = !!permission && permission.hasPermission("users", "edit");
  const [reverting, setReverting] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null); // { record, step } while the note dialog is open
  const [revertNote, setRevertNote] = useState("");
  const [revertHistory, setRevertHistory] = useState([]);
  const [phoneStep, setPhoneStep] = useState(null); // why step 4 is open or locked for this user
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("account");
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  const fetchUser = async (hideLoader = false) => {
    if (!hideLoader) {
      setLoading(true);
      setActiveTab("account");
    }
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${baseUrl}/users/full-details/${userId}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const result = await response.json();
      if (result?.status === true && result?.data) {
        setData(result.data);
      } else {
        throw new Error(result?.message || "Failed to fetch user details");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (!hideLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // The user's KYC as the app sees it: past reverts (who, when, which step,
  // the note) and why phone verification is open or locked for them.
  const loadRevertHistory = async () => {
    try {
      const res = await fetchUserKyc(userId);
      setRevertHistory(Array.isArray(res?.data?.reverts) ? res.data.reverts : []);
      setPhoneStep(res?.data?.phoneStep || null);
    } catch {
      setRevertHistory([]);
      setPhoneStep(null);
    }
  };

  useEffect(() => {
    if (activeTab === "kyc") loadRevertHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userId]);

  // Put a completed KYC step back to not done (and every step after it), for
  // re-testing a flow or for support. A note is required; the backend logs it
  // with who did it.
  const noteOk = revertNote.trim().length >= REVERT_NOTE_MIN;
  const confirmRevert = async () => {
    if (!revertTarget || !noteOk) return;
    const { record, step } = revertTarget;
    setReverting(true);
    try {
      const res = await revertUserKycStep(userId, step.n, record.id, revertNote.trim());
      toast.success(res.message || "Reverted");
      setRevertTarget(null);
      setRevertNote("");
      await fetchUser(true);
      await loadRevertHistory();
    } catch (err) {
      toast.error(err.message || "Could not revert the step");
    } finally {
      setReverting(false);
    }
  };

  const user = data?.userInfo || {};
  const account = data?.account || {};
  const mining = data?.miningActivity || {};
  const creatorHub = data?.creatorHub || {};
  const referrals = data?.referrals || {};
  const strikeHistory = data?.strikeHistory || [];
  const pioneerRequests = data?.pioneerRequests || [];
  const kyc = data?.kyc || [];
  const supportRequests = data?.supportRequests || [];
  const deviceTokens = data?.deviceTokens || [];
  const activityTimeline = data?.activityTimeline || [];

  const avatarLetter = (user.username || "U")[0].toUpperCase();

  // A user is a Pioneer when their latest request was approved — the same
  // reading the app uses. Rows come newest first.
  const pioneerStatus = pioneerRequests[0]?.status || null;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 w-full space-y-5">

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-600 transition"
          >
            <FiArrowLeft size={15} /> Back
          </button>

          {loading ? (
            <div className="flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">{error}</div>
          ) : data ? (
            <>
              {/* Profile header */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                    {avatarLetter}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-800 flex flex-wrap items-center gap-2">
                      {user.username || "—"}
                      {pioneerStatus === "approved" && (
                        <button
                          type="button"
                          onClick={() => setActiveTab("pioneer")}
                          title="Approved Pioneer — open their Pioneer request"
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200 transition"
                        >
                          <FiAward size={12} />
                          Pioneer
                        </button>
                      )}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
                    <p className="text-xs text-gray-400 font-mono mt-1">{user.uniqueCode}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge value={user.isMiningActive} trueLabel="Mining Active" falseLabel="Mining Inactive" trueColor="green" />
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.providerType === "google" ? "bg-red-100 text-red-600"
                        : user.providerType === "apple" ? "bg-gray-100 text-gray-600"
                        : "bg-blue-100 text-blue-600"
                      }`}>
                        {user.providerType || "—"}
                      </span>
                      {user.strike > 0 && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600">
                          {user.strike} Strike{user.strike > 1 ? "s" : ""}
                        </span>
                      )}
                      {pioneerStatus === "pending" && (
                        <button
                          type="button"
                          onClick={() => setActiveTab("pioneer")}
                          title="Has a Pioneer request waiting for review"
                          className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 transition"
                        >
                          Pioneer request pending
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right flex flex-col items-end gap-2">
                    <div>
                      <p className="text-xs text-gray-400">User ID</p>
                      <p className="text-2xl font-bold text-gray-700">#{user.userId}</p>
                      <p className="text-xs text-gray-400 mt-1">Joined {formatDate(account.joinedAt || user.createdAt, false)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsNotificationModalOpen(true);
                      }}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition text-xs font-semibold"
                    >
                      <FiBell size={13} /> Send Notification
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard label="Total Coins" value={parseFloat(user.coin || 0).toFixed(2)} color="blue" />
                <StatCard label="Total Mined" value={parseFloat(mining.totalMinedCoin || 0).toFixed(2)} sub={`${mining.totalSessions ?? 0} sessions`} color="green" />
                <StatCard label="Strike Count" value={user.strike ?? 0} color="orange" />
                <StatCard label="Referrals" value={referrals.totalCount ?? 0} sub={`${referrals.activeCount ?? 0} active`} color="purple" />
              </div>

              {/* Tabs */}
              <div className="flex flex-wrap gap-1 bg-white border border-gray-100 rounded-xl p-1 shadow-sm">
                {TABS.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition ${
                        activeTab === tab.key
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <TabIcon size={13} /> {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Account Information */}
              {activeTab === "account" && (
                <Section title="Account Information" icon={FiUser} iconBg="bg-blue-50" iconColor="text-blue-500">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                    <Field label="User ID" value={user.userId} />
                    <Field label="Unique Code" value={user.uniqueCode} mono />
                    <Field label="Username" value={user.username} />
                    <Field label="Email" value={user.email} />
                    <Field label="Provider Type" value={user.providerType} />
                    <Field label="Referral Code" value={user.referralCode} mono />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Shareable Link</p>
                      {user.referralLink ? (
                        <div className="flex items-center gap-1.5">
                          <p className="font-mono text-xs text-blue-700 break-all">{user.referralLink}</p>
                          <button
                            type="button"
                            title="Copy link"
                            onClick={() => {
                              navigator.clipboard.writeText(user.referralLink).then(
                                () => toast.success("Referral link copied"),
                                () => toast.error("Could not copy the link")
                              );
                            }}
                            className="text-gray-400 hover:text-blue-500 transition flex-shrink-0"
                          >
                            <FiCopy size={13} />
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">Not configured on the backend</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Referred By</p>
                      {account.referredBy ? (
                        <button
                          onClick={() => navigate(`/user-full-details/${account.referredBy.userId}`, { state: location.state })}
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          {account.referredBy.username || account.referredBy.email}
                        </button>
                      ) : (
                        <p className="text-sm font-medium text-gray-800 break-all">{user.referralBy || "—"}</p>
                      )}
                    </div>
                    <Field label="Account Age" value={account.accountAgeDays != null ? `${account.accountAgeDays} days` : null} />
                    <Field label="Last Activity" value={formatDate(account.lastActivityAt)} />
                    <Field label="Total Activity Events" value={account.totalActivityEvents} />
                    <Field label="Created At" value={formatDate(user.createdAt)} />
                  </div>
                </Section>
              )}

              {/* Mining Activity */}
              {activeTab === "mining" && (
                <Section title="Mining Activity" icon={FiZap} iconBg="bg-yellow-50" iconColor="text-yellow-500">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 mb-5">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Status</p>
                      <Badge value={mining.isMiningActive} trueLabel="Active" falseLabel="Inactive" trueColor="green" />
                    </div>
                    <Field label="Total Sessions" value={mining.totalSessions} />
                    <Field label="Total Mined Coin" value={parseFloat(mining.totalMinedCoin || 0).toFixed(5)} />
                  </div>
                  {mining.currentSession && Object.keys(mining.currentSession).length > 0 && (
                    <div className="border border-gray-100 rounded-xl p-4 mb-5">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Current Session</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                        <Field label="Session ID" value={mining.currentSession.id} />
                        <Field label="Started" value={formatDate(mining.currentSession.startDateAndTime)} />
                        <Field label="Ends" value={formatDate(mining.currentSession.endDateAndTime)} />
                        <Field label="Current Coin" value={mining.currentSession.curruntCoin} />
                      </div>
                    </div>
                  )}
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Session History</p>
                  <SimpleTable
                    rowKey="id"
                    rows={mining.history}
                    scrollable
                    columns={[
                      { key: "id", label: "ID" },
                      { key: "startDateAndTime", label: "Started", render: (r) => formatDate(r.startDateAndTime) },
                      { key: "endDateAndTime", label: "Ended", render: (r) => formatDate(r.endDateAndTime) },
                      { key: "curruntCoin", label: "Coin", align: "right" },
                      { key: "updatedCoin", label: "Updated Coin", align: "right" },
                      { key: "isActive", label: "Active", render: (r) => <Badge value={r.isActive} trueColor="green" /> },
                    ]}
                  />
                </Section>
              )}

              {/* Creator Hub */}
              {activeTab === "creatorHub" && (
                <Section title="Creator Hub" icon={FiLayers} iconBg="bg-purple-50" iconColor="text-purple-500">
                   <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Profile Verification Links</p>
                  {(() => {
                    const links = creatorHub.profileVerificationLinks || [];
                    if (links.length === 0) {
                      return <p className="text-sm text-gray-400 mb-5">No profile verification links available.</p>;
                    }

                    const map = new Map();
                    for (const item of links) {
                      const key = item.id;
                      if (map.has(key)) {
                        const existing = map.get(key);
                        PLATFORMS.forEach(p => {
                          if (item[p.urlKey] && !existing[p.urlKey]) {
                            existing[p.urlKey] = item[p.urlKey];
                            existing[p.statusKey] = item[p.statusKey];
                            existing[p.codeKey] = item[p.codeKey];
                            existing[p.codeSentKey] = item[p.codeSentKey];
                            existing[p.codeVerifiedKey] = item[p.codeVerifiedKey];
                            existing[p.codeSendDateKey] = item[p.codeSendDateKey];
                            existing[p.codeVerifDateKey] = item[p.codeVerifDateKey];
                          }
                        });
                      } else {
                        map.set(key, { ...item });
                      }
                    }
                    const groupedLinks = Array.from(map.values()).sort((a, b) => b.id - a.id);

                    return (
                      <div className="space-y-6 mb-5">
                        {groupedLinks.map((link) => (
                          <div key={link.id}>
                            <PlatformVerificationCards
                              detail={link}
                              onActionComplete={(updatedDetail) => {
                                setData((prev) => {
                                  if (!prev) return prev;
                                  const newCreatorHub = { ...prev.creatorHub };
                                  if (newCreatorHub.profileVerificationLinks) {
                                    newCreatorHub.profileVerificationLinks = newCreatorHub.profileVerificationLinks.map(l =>
                                      l.id === updatedDetail.id ? updatedDetail : l
                                    );
                                  }
                                  return { ...prev, creatorHub: newCreatorHub };
                                });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Applications</p>
                  <div className="mb-5">
                    <UserApplicationsTable
                      applications={creatorHub.applications}
                      onActionComplete={() => fetchUser(true)}
                    />
                  </div>
                </Section>
              )}

              {/* Referral */}
              {activeTab === "referral" && (
                <Section title="Referral" icon={FiGitBranch} iconBg="bg-green-50" iconColor="text-green-500">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 mb-5">
                    <Field label="Total Referrals" value={referrals.totalCount} />
                    <Field label="Active Referrals" value={referrals.activeCount} />
                  </div>
                  <SimpleTable
                    rowKey="userId"
                    rows={referrals.users}
                    scrollable
                    columns={[
                      { key: "username", label: "Username", render: (r) => r.userId ? (
                        <button
                          onClick={() => navigate(`/user-full-details/${r.userId}`, { state: location.state })}
                          className="text-gray-800 hover:text-blue-600 hover:underline transition font-medium"
                        >
                          {r.username || "—"}
                        </button>
                      ) : (r.username || "—") },
                      { key: "email", label: "Email", render: (r) => r.userId ? (
                        <button
                          onClick={() => navigate(`/user-full-details/${r.userId}`, { state: location.state })}
                          className="text-gray-600 hover:text-blue-600 hover:underline transition"
                        >
                          {r.email || "—"}
                        </button>
                      ) : (r.email || "—") },
                      { key: "createdAt", label: "Joined", render: (r) => formatDate(r.createdAt, false) },
                    ]}
                  />
                </Section>
              )}

              {/* Strike History */}
              {activeTab === "strikes" && (
                <Section title="Strike History" icon={FiAlertTriangle} iconBg="bg-orange-50" iconColor="text-orange-500">
                  <SimpleTable
                    rowKey="id"
                    rows={strikeHistory}
                    scrollable
                    columns={[
                      { key: "id", label: "ID" },
                      { key: "strikeCount", label: "Strike Count" },
                      { key: "date", label: "Date", render: (r) => formatDate(r.date, false) },
                    ]}
                  />
                </Section>
              )}

              {/* Pioneer Requests */}
              {activeTab === "pioneer" && (
                <Section title="Pioneer Requests" icon={FiFlag} iconBg="bg-amber-50" iconColor="text-amber-500">
                  <SimpleTable
                    rowKey="id"
                    rows={pioneerRequests}
                    scrollable
                    columns={[
                      { key: "name", label: "Name", render: (r) => [r.firstName, r.lastName].filter(Boolean).join(" ") || "—" },
                      { key: "email", label: "Email" },
                      { key: "country", label: "Country" },
                      { key: "profession", label: "Profession" },
                      { key: "url", label: "URL", render: (r) => r.url ? (
                        <a href={safeUrl(r.url)} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">Link</a>
                      ) : "—" },
                      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
                      { key: "createdAt", label: "Submitted", render: (r) => formatDate(r.createdAt) },
                    ]}
                  />
                </Section>
              )}

              {/* KYC */}
              {activeTab === "kyc" && (
                <Section title="KYC" icon={FiShield} iconBg="bg-teal-50" iconColor="text-teal-500" empty={kyc.length === 0} emptyLabel="KYC has not been started for this user.">
                  <div className="space-y-6">
                    {kyc.map((record) => (
                      <div key={record.id} className="border border-gray-100 rounded-xl overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                            <span className="text-xs text-gray-500">
                              Current step <span className="font-semibold text-gray-700">{record.currentStep ?? 0} / 6</span>
                            </span>
                            <span className="text-xs text-gray-500">Started {formatDate(record.createdAt)}</span>
                            <span className="text-xs text-gray-500">Last updated {formatDate(record.updatedAt)}</span>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {KYC_STEPS.map((step) => {
                            const status = record[`step${step.n}Status`];
                            const completedAt = record[`step${step.n}CompletedAt`];
                            const coin = record[`step${step.n}completionCoin`];
                            return (
                              <div key={step.n} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-700">Step {step.n} · {step.label}</p>
                                  {step.desc && <p className="text-xs text-gray-400 mt-0.5">{step.desc}</p>}
                                </div>
                                <div className="flex items-center gap-4 flex-shrink-0">
                                  {status === "completed" && (
                                    <span className="text-xs text-gray-400">{formatDate(completedAt)}</span>
                                  )}
                                  {status === "completed" && coin != null && coin !== "" && (
                                    <span className="text-xs font-medium text-emerald-600">+{parseFloat(coin).toFixed(4)} coin</span>
                                  )}
                                  <KycStepBadge status={status} />
                                  {/* Step 4 also when not completed: a cancelled or failed code
                                      leaves it open with a wait / old codes the revert clears. */}
                                  {canRevertKyc &&
                                    (status === "completed" || (step.n === 4 && status && status !== "locked")) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRevertNote("");
                                        setRevertTarget({ record, step });
                                      }}
                                      disabled={reverting}
                                      aria-label={`Revert step ${step.n}`}
                                      className="px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg disabled:opacity-40"
                                    >
                                      Revert
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {phoneStep && (
                      <div className="border border-gray-100 rounded-xl p-4 space-y-2" data-testid="phone-step-diagnosis">
                        <p className="text-sm font-semibold text-gray-700">
                          Phone verification (step 4) for this user:{" "}
                          <span
                            className={
                              phoneStep.result === "open"
                                ? "text-emerald-600"
                                : phoneStep.result === "completed"
                                ? "text-blue-600"
                                : "text-red-600"
                            }
                          >
                            {phoneStep.result === "open" ? "Open" : phoneStep.result === "completed" ? "Completed" : "Locked"}
                          </span>
                        </p>
                        <ul className="space-y-1">
                          {phoneStep.checks.map((c) => (
                            <li key={c.key} className="flex items-start gap-2 text-xs">
                              {c.ok ? (
                                <FiCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" aria-label="ok" />
                              ) : (
                                <FiXCircle className="text-red-500 mt-0.5 flex-shrink-0" aria-label="problem" />
                              )}
                              <span className={c.ok ? "text-gray-600" : "text-red-600"}>{c.message}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {revertHistory.length > 0 && (
                      <div className="border border-gray-100 rounded-xl p-4 space-y-2" data-testid="revert-history">
                        <p className="text-sm font-semibold text-gray-700">Revert history</p>
                        <div className="divide-y divide-gray-50">
                          {revertHistory.map((r) => (
                            <div key={r.id} className="py-2 text-xs">
                              <div className="text-gray-500">
                                {formatDate(r.at)} · {r.admin || "Unknown admin"} · reverted from step {r.step ?? "?"}
                              </div>
                              <div className={`mt-0.5 ${r.note ? "text-gray-800" : "text-gray-400 italic"}`}>
                                {r.note || "No note (reverted before notes were required)"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {canSeePhone && (
                      <div className="border border-gray-100 rounded-xl p-4 space-y-2">
                        <p className="text-sm font-semibold text-gray-700">Phone verification (SMS)</p>
                        <PhoneUserPanel userId={userId} />
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Support Requests */}
              {activeTab === "support" && (
                <Section title="Support Requests" icon={FiHelpCircle} iconBg="bg-indigo-50" iconColor="text-indigo-500">
                  <SimpleTable
                    rowKey="id"
                    rows={supportRequests}
                    columns={[
                      { key: "id", label: "ID" },
                      { key: "subject", label: "Subject" },
                      { key: "message", label: "Message" },
                      { key: "createdAt", label: "Created", render: (r) => formatDate(r.createdAt) },
                    ]}
                  />
                </Section>
              )}

              {/* Device Tokens */}
              {activeTab === "devices" && (
                <Section title="Device Tokens" icon={FiSmartphone} iconBg="bg-blue-50" iconColor="text-blue-500">
                  <SimpleTable
                    rowKey="id"
                    rows={deviceTokens}
                    columns={[
                      { key: "id", label: "ID" },
                      { key: "deviceId", label: "Device ID", render: (r) => <span className="font-mono text-xs">{r.deviceId}</span> },
                      { key: "deviceType", label: "Device Type", render: (r) => <span className="capitalize">{r.deviceType}</span> },
                      { key: "createdAt", label: "Registered", render: (r) => formatDate(r.createdAt) },
                      { key: "updatedAt", label: "Last Updated", render: (r) => formatDate(r.updatedAt) },
                    ]}
                  />
                </Section>
              )}

              {/* Activity Timeline */}
              {activeTab === "activity" && (
                <Section title="Activity Timeline" icon={FiActivity} iconBg="bg-gray-50" iconColor="text-gray-500" empty={activityTimeline.length === 0}>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                    {activityTimeline.map((ev, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5" />
                          {idx < activityTimeline.length - 1 && <div className="flex-1 w-px bg-gray-100 mt-1" />}
                        </div>
                        <div className="pb-3">
                          <p className="text-sm text-gray-700">{ev.description || ev.type}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{formatDate(ev.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              {/* Notification History */}
              {activeTab === "notificationHistory" && (
                <Section title="Notification History" icon={FiBell} iconBg="bg-amber-50" iconColor="text-amber-500">
                  <NotificationHistoryTab userId={user.userId} />
                </Section>
              )}
            </>
          ) : (
            <p className="text-gray-500">No user data available.</p>
          )}
        </div>
      </div>
      <SendNotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        userId={userId}
      />
      {revertTarget && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => !reverting && setRevertTarget(null)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Revert step ${revertTarget.step.n}`}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-gray-800">
              Revert step {revertTarget.step.n} · {revertTarget.step.label}
            </h3>
            <p className="text-xs text-gray-600">
              {revertTarget.step.n === 6 ? "Step 6 goes" : `Steps ${revertTarget.step.n}–6 go`} back to not done.
              {revertTarget.step.n <= 4 && " Their verified phone number is released."}
              {revertTarget.step.n <= 2 && " Steps 1–2 are checked automatically: the app completes them again if the user still qualifies."}
            </p>
            <label className="block space-y-1 text-xs">
              <span className="font-medium text-gray-700">Note (required)</span>
              <textarea
                aria-label="Revert note"
                value={revertNote}
                maxLength={REVERT_NOTE_MAX}
                rows={3}
                autoFocus
                onChange={(e) => setRevertNote(e.target.value)}
                placeholder="Why is this step reverted? e.g. re-testing the phone flow"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <span className="text-[10px] text-gray-400">
                {revertNote.trim().length}/{REVERT_NOTE_MAX} · at least {REVERT_NOTE_MIN} characters · saved in the activity log
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRevertTarget(null)}
                disabled={reverting}
                className="px-3 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRevert}
                disabled={!noteOk || reverting}
                className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl disabled:opacity-40"
              >
                {reverting ? "Reverting…" : `Revert step ${revertTarget.step.n}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFullDetail;
